import { ChevronRight, X as CloseIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { RootState } from '~/redux/store';
import UserProfile from './UserProfile';
import { getVisibleNavSections, type NavItem } from '~/layout/config';
import { useGetSellerOrdersCountByStatus } from '~/api';
import { isSellerCapable } from '~/utils';
import GofariLogo from '~/assets/svgs/gofari-logo-reverse.svg';

interface SideNavProps {
  onNavigate?: () => void;
}

export default function SideNav({ onNavigate }: SideNavProps) {
  const { t } = useTranslation('general');
  const user = useSelector((state: RootState) => state.auth.user);
  const role = user?.role;
  const sellerCapable = isSellerCapable(role);
  // Refresh on window focus (after the 30s staleTime) so a seller who confirmed
  // via magic link in another tab, or was reminded by the daily job, sees the
  // badge update on return — without refetching on every alt-tab.
  const { data: orderCountsData } = useGetSellerOrdersCountByStatus({
    enabled: sellerCapable,
    refetchOnWindowFocus: true,
  });
  const inboxReminderCount =
    orderCountsData?.sellerOrdersCount
      ?.pendingSellerConfirmationWithReminders ?? 0;
  const visibleSections = getVisibleNavSections(role);

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    if (item.external) {
      return (
        <li key={item.id}>
          <a
            href={item.to}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-neut-prim hover:bg-neut-bg"
          >
            <Icon size={18} />
            <span className="flex-1 text-left">{t(item.labelKey)}</span>
          </a>
        </li>
      );
    }
    return (
      <li key={item.id}>
        <NavLink
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) => `
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
            transition-colors
            ${isActive ? 'bg-nav-active-bg text-neut-0' : 'text-neut-prim hover:bg-neut-bg'}
          `}
        >
          {({ isActive }) => (
            <>
              <Icon size={18} />
              <span className="flex-1 text-left">{t(item.labelKey)}</span>
              {item.id === 'inbox' && inboxReminderCount > 0 && (
                <span
                  aria-label={t('nav.inbox_reminder_badge', {
                    count: inboxReminderCount,
                  })}
                  className="bg-prim-100 text-prim-700 text-xs font-medium rounded-full px-2 py-1"
                >
                  {inboxReminderCount}
                </span>
              )}
              {isActive && <ChevronRight size={16} className={'text-neut-0'} />}
            </>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <aside className="w-full h-full bg-nav-bg flex flex-col justify-between relative">
      <div className="flex flex-col items-start justify-between px-6 pt-6 pb-4 bg-nav-active-bg relative">
        <h1 className="leading-[36px]">
          <img src={GofariLogo} alt="Gofari" className="h-6 w-auto" />
        </h1>
        <p className="text-sm text-neut-0/70 capitalize font-normal leading-[20px]">
          {role} Portal
        </p>

        <CloseIcon
          onClick={onNavigate}
          color="var(--color-neut-0)"
          className="lg:hidden cursor-pointer absolute top-1 right-6"
        />
      </div>

      <nav className="pt-3 pb-6 px-3  flex flex-col w-full gap-4">
        {visibleSections.map((section) => (
          <div key={section.id} className="w-full flex flex-col gap-1">
            {section.headingKey && (
              <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-neut-sec">
                {t(section.headingKey)}
              </p>
            )}
            <ul className="w-full flex flex-col gap-1">
              {section.items.map(renderItem)}
            </ul>
          </div>
        ))}
      </nav>
      <UserProfile />
    </aside>
  );
}
